import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Empty, ErrorBanner, Panel, Spinner } from '../lib/ui';

/**
 * Team management, modelled on the Billboard Intelligence users panel
 * (`web/app.js`, `openUsersModal`) so the two tools feel like one system: add a
 * teammate, reset a password, remove access, with the current user's own row
 * protected.
 *
 * Two deliberate departures from BBI, both keeping its shape but tightening the
 * behaviour:
 *
 * 1. **Passwords are generated here, not typed.** BBI asks the admin to invent
 *    one (minimum six characters) and send it onward somehow. This issues a
 *    random temporary password, shows it exactly once, and forces a change at
 *    first sign-in — so an admin never learns a colleague's working password and
 *    a weak one cannot be chosen on their behalf.
 * 2. **Removal deactivates rather than deletes.** Every audit row names its
 *    actor; deleting the user would leave those rows pointing at nobody.
 *    Deactivating cuts access just as immediately — the session epoch is bumped,
 *    so live sessions die on their next request — while keeping the history
 *    readable.
 */

interface AppUser {
  username: string;
  displayName: string | null;
  email: string | null;
  isAdmin: boolean;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: number | null;
}

/** A temporary password, shown once and never retrievable again. */
interface Issued {
  username: string;
  password: string;
}

export function UsersPage({ me }: { me: string }) {
  const [rows, setRows] = useState<AppUser[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);

  const [newUsername, setNewUsername] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);

  const load = useCallback(() => {
    api
      .get<{ users: AppUser[] }>('/api/auth/users')
      .then((d) => {
        setRows(d.users);
        setError(null);
      })
      .catch((e: unknown) => setError(e));
  }, []);

  useEffect(load, [load]);

  async function addUser(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ username: string; temporaryPassword: string }>(
        '/api/auth/users',
        {
          username: newUsername.trim(),
          displayName: newDisplayName.trim() === '' ? null : newDisplayName.trim(),
          isAdmin: newIsAdmin,
        },
      );
      setIssued({ username: res.username, password: res.temporaryPassword });
      setNewUsername('');
      setNewDisplayName('');
      setNewIsAdmin(false);
      load();
    } catch (err: unknown) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function patch(username: string, body: Record<string, unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch<{ temporaryPassword?: string }>(
        `/api/auth/users/${encodeURIComponent(username)}`,
        body,
      );
      if (res.temporaryPassword !== undefined) {
        setIssued({ username, password: res.temporaryPassword });
      }
      load();
    } catch (err: unknown) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function resetPassword(username: string): void {
    if (
      !window.confirm(
        `Issue a new temporary password for ${username}?\n\nTheir current password stops working immediately and every session they have open is signed out.`,
      )
    )
      return;
    void patch(username, { resetPassword: true });
  }

  function setActive(username: string, isActive: boolean): void {
    const verb = isActive ? 'Restore access for' : 'Remove access for';
    if (!window.confirm(`${verb} ${username}?`)) return;
    void patch(username, { isActive });
  }

  return (
    <div className="space-y-4">
      {error !== null && <ErrorBanner error={error} />}

      {/* Shown once. There is no way back to this value — the server stores only
          a hash — so it is deliberately loud and has to be dismissed by hand
          rather than disappearing on the next render. */}
      {issued !== null && (
        <div className="border-2 border-harkey-gold bg-amber-50 p-3 dark:bg-amber-950">
          <p className="text-xs font-semibold">
            Temporary password for {issued.username}
          </p>
          <p className="my-2 select-all font-mono text-lg tracking-tight">{issued.password}</p>
          <p className="text-xs text-neutral-700 dark:text-neutral-300">
            Shown once and not recoverable. Give it to them directly — they must choose a new
            password before they can use anything.
          </p>
          <button
            onClick={() => setIssued(null)}
            className="mt-2 border border-neutral-400 px-2 py-0.5 text-xs hover:bg-white dark:hover:bg-neutral-900"
          >
            I've written it down
          </button>
        </div>
      )}

      <Panel title="Add a teammate">
        <form onSubmit={addUser} className="flex flex-wrap items-end gap-2">
          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">
              Username (work email)
            </span>
            <input
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="j.smith@harkeymedia.com"
              autoCapitalize="off"
              spellCheck={false}
              required
              className="w-64 border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-neutral-600 dark:text-neutral-400">Display name</span>
            <input
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              placeholder="Jane Smith"
              className="w-48 border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950"
            />
          </label>
          <label className="flex items-center gap-1.5 pb-1 text-xs">
            <input
              type="checkbox"
              checked={newIsAdmin}
              onChange={(e) => setNewIsAdmin(e.target.checked)}
            />
            Admin
          </label>
          <button
            type="submit"
            disabled={busy || newUsername.trim() === ''}
            className="bg-harkey-gold px-3 py-1 text-xs font-semibold text-black disabled:opacity-50"
          >
            Add user
          </button>
        </form>
        <p className="mt-2 text-xs text-neutral-500">
          A temporary password is generated and shown once. Admins can manage users; everyone else
          gets the rest of the app.
        </p>
      </Panel>

      <Panel title={`Team (${rows?.length ?? 0})`}>
        {rows === null ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty>No users yet.</Empty>
        ) : (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-neutral-500 dark:border-neutral-800">
                <th className="py-1 pr-3 font-medium">User</th>
                <th className="py-1 pr-3 font-medium">Role</th>
                <th className="py-1 pr-3 font-medium">Last sign-in</th>
                <th className="py-1 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const isSelf = u.username === me;
                return (
                  <tr
                    key={u.username}
                    className={`border-b border-neutral-100 dark:border-neutral-900 ${
                      u.isActive ? '' : 'opacity-50'
                    }`}
                  >
                    <td className="py-1.5 pr-3">
                      <span className="font-medium">{u.displayName ?? u.username}</span>
                      {isSelf && <span className="ml-1 text-neutral-500">(you)</span>}
                      <span className="block text-neutral-500">{u.username}</span>
                    </td>
                    <td className="py-1.5 pr-3">
                      {u.isAdmin && (
                        <span className="mr-1 bg-harkey-gold px-1.5 py-0.5 font-semibold text-black">
                          admin
                        </span>
                      )}
                      {!u.isActive && <span className="text-red-600">no access</span>}
                      {u.isActive && u.mustChangePassword && (
                        <span className="text-neutral-500">temporary password</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-neutral-500">{sinceText(u.lastLoginAt)}</td>
                    <td className="py-1.5">
                      <button
                        onClick={() => resetPassword(u.username)}
                        disabled={busy}
                        className="mr-2 border border-neutral-300 px-1.5 py-0.5 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
                      >
                        Reset password
                      </button>
                      {/* Guarded in the API too — an admin removing their own
                          access is unrecoverable without database access — but
                          disabled here so it never looks available. */}
                      <button
                        onClick={() => setActive(u.username, !u.isActive)}
                        disabled={busy || isSelf}
                        title={isSelf ? 'You cannot remove your own access' : undefined}
                        className={`border px-1.5 py-0.5 disabled:opacity-40 ${
                          u.isActive
                            ? 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950'
                            : 'border-neutral-300 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800'
                        }`}
                      >
                        {u.isActive ? 'Remove access' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

/** Relative time, matching the BBI panel's "last seen" phrasing. */
function sinceText(ms: number | null): string {
  if (ms === null) return 'never';
  const seconds = (Date.now() - ms) / 1000;
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
